/**
 * Generation progress + result screen (P1-T15) — `#/result/:jobId`.
 *
 * Lifecycle: fetch the job → poll `GET /ai/jobs/:jobId` every 2.5s while the
 * state is queued/processing → render the terminal state:
 *
 *   done              → media preview (image, or tap-to-play video that
 *                       pauses when scrolled off-screen), Download, Share,
 *                       "Saved to Collection ✓" + link, "Try again".
 *   failed/cancelled  → friendly error + "credits refunded" note + Try again.
 *   404               → not-found screen (bad jobId).
 *   403               → generic "not available" screen (never leaks existence).
 *   network error     → error screen with retry.
 *
 * Polling cancels when the user navigates away (sequence guard + timer
 * cleanup). On the first terminal poll the credit balance is refreshed —
 * the ✦ hold is either captured or released at that point.
 */
import { ApiError, api } from '../api/client.js';
import { navigate } from '../router.js';
import { escapeHtml } from '../components/placeholder.js';
import { icon } from '../components/icon.js';
import { bindCompareSliders, compareSliderHtml } from '../components/compareSlider.js';
import { toast, toastApiError } from '../ui/toast.js';
import { refreshCredits } from '../state/credits.js';
import type { AiJob, JobState, UploadFileMeta } from '@loomia/shared';

const POLL_MS = 2500;

type Terminal = 'done' | 'failed' | 'cancelled';

/** Single-photo image tools: the result page shows a Before/After slider. */
const COMPARE_TOOLS: ReadonlySet<string> = new Set([
  'photo_enhance',
  'hair_salon',
  'makeup_styles',
  'face_expression',
  'change_outfit',
  'edit_image',
  'retouch',
  'remove_object',
  'reshape_body',
]);

function isTerminal(state: JobState): state is Terminal {
  return state === 'done' || state === 'failed' || state === 'cancelled';
}

function toolLabel(tool: string): string {
  return tool
    .split(/[-_]+/)
    .map((w) => (w ? w[0]!.toUpperCase() + w.slice(1) : w))
    .join(' ');
}

/** image/jpeg → jpg, video/mp4 → mp4, … */
function extFor(contentType: string): string {
  const sub = contentType.split('/')[1]?.split(';')[0]?.trim().toLowerCase() ?? 'bin';
  if (sub === 'jpeg') return 'jpg';
  return sub.replace(/[^a-z0-9]/g, '') || 'bin';
}

type ShareNavigator = Navigator & {
  share?: (data: { title?: string; text?: string; url?: string }) => Promise<void>;
};

let renderSeq = 0;
/** Cleanup for the previous result mount (poll timer, observers). */
let prevCleanup: (() => void) | null = null;

export function renderResult(root: HTMLElement, jobId: string): void {
  const seq = ++renderSeq;
  const alive = (): boolean => seq === renderSeq && root.isConnected;

  if (prevCleanup) {
    prevCleanup();
    prevCleanup = null;
  }

  let timer: ReturnType<typeof setTimeout> | null = null;
  let observer: IntersectionObserver | null = null;
  let balanceRefreshed = false;

  const stop = (): void => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    if (observer) {
      observer.disconnect();
      observer = null;
    }
  };
  prevCleanup = stop;

  const goBack = (): void => {
    if (window.history.length > 1) window.history.back();
    else navigate('/home');
  };

  const bindBack = (): void => {
    root.querySelector('[data-action="back"]')?.addEventListener('click', goBack);
  };

  const refreshBalanceOnce = (): void => {
    if (balanceRefreshed) return;
    balanceRefreshed = true;
    // The ✦ hold is captured or released at the terminal transition.
    void refreshCredits().catch(() => undefined);
  };

  // -- Static states --------------------------------------------------------

  const loadingHtml = (): void => {
    root.innerHTML = `
      <div class="rs" aria-busy="true" aria-label="Loading generation">
        <header class="rs-head">
          <button class="ed-back" data-action="back" aria-label="Back">←</button>
          <h1 class="ed-title">Result</h1>
          <span class="ed-head-spacer" aria-hidden="true"></span>
        </header>
        <div class="stack rs-body">
          <div class="rs-media is-skeleton"><span class="shimmer"></span></div>
          <span class="shimmer-line"></span>
          <span class="shimmer-line short"></span>
        </div>
      </div>`;
    bindBack();
  };

  /** kind: 'notfound' | 'forbidden' | 'network' */
  const loadErrorHtml = (kind: 'notfound' | 'forbidden' | 'network'): void => {
    const copy =
      kind === 'notfound'
        ? 'We couldn\u2019t find this generation. It may have been deleted.'
        : kind === 'forbidden'
          ? 'This generation isn\u2019t available.'
          : 'Couldn\u2019t reach the server. Check your connection.';
    root.innerHTML = `
      <div class="rs">
        <header class="rs-head">
          <button class="ed-back" data-action="back" aria-label="Back">←</button>
          <h1 class="ed-title">Result</h1>
          <span class="ed-head-spacer" aria-hidden="true"></span>
        </header>
        <div class="stack">
          <div class="empty" style="padding-top: 72px;">
            <div class="empty-icon">${kind === 'network' ? '📡' : '🔍'}</div>
            <p class="muted" style="max-width: 280px;">${escapeHtml(copy)}</p>
            ${
              kind === 'network'
                ? '<button class="btn btn-secondary" data-action="retry">Try again</button>'
                : '<a class="btn btn-secondary" href="#/home">Back home</a>'
            }
          </div>
        </div>
      </div>`;
    bindBack();
    if (kind === 'network') {
      root
        .querySelector('[data-action="retry"]')
        ?.addEventListener('click', () => void boot());
    }
  };

  // -- Active (progress) ----------------------------------------------------

  const paintActive = (job: AiJob): void => {
    const pct = typeof job.progress === 'number' ? Math.max(0, Math.min(100, job.progress)) : null;
    const processing = job.state === 'processing';
    const headline = processing ? 'Creating…' : 'In queue';
    const sub = processing
      ? `Working on your ${toolLabel(job.tool)}${job.qualityTier ? ` · ${job.qualityTier.toUpperCase()}` : ''}`
      : 'Your creation is up next.';
    root.innerHTML = `
      <div class="rs" aria-busy="true" aria-label="Generation in progress">
        <header class="rs-head">
          <button class="ed-back" data-action="back" aria-label="Back">←</button>
          <h1 class="ed-title">Creating</h1>
          <span class="ed-head-spacer" aria-hidden="true"></span>
        </header>
        <div class="stack rs-body">
          <div class="rs-progress-card">
            <div class="rs-spinner" aria-hidden="true"></div>
            <h2 class="rs-headline">${escapeHtml(headline)}</h2>
            <p class="muted rs-sub">${escapeHtml(sub)}</p>
            ${
              pct !== null
                ? `
              <div class="rs-bar" role="progressbar" aria-valuenow="${Math.round(pct)}" aria-valuemin="0" aria-valuemax="100" aria-label="Generation progress">
                <span style="width: ${pct}%;"></span>
              </div>
              <p class="rs-pct">${Math.round(pct)}%</p>`
                : `
              <div class="rs-bar is-indeterminate" aria-hidden="true"><span></span></div>`
            }
            <p class="rs-hold"><span aria-hidden="true">✦</span> ${job.creditCost} reserved
              <span class="rs-hold-note">Charged only if generation succeeds.</span>
            </p>
          </div>
          <p class="muted rs-tip">You can leave this screen — your result will be waiting in Collection.</p>
        </div>
      </div>`;
    bindBack();
  };

  // -- Done -----------------------------------------------------------------

  const showDone = async (job: AiJob): Promise<void> => {
    let meta: UploadFileMeta | null = null;
    try {
      meta = await api.getUpload(job.resultFileId ?? '');
    } catch (err) {
      if (!alive()) return;
      toastApiError(err, 'Result finished, but the file is not available yet.');
      root.innerHTML = `
        <div class="rs">
          <header class="rs-head">
            <button class="ed-back" data-action="back" aria-label="Back">←</button>
            <h1 class="ed-title">Result</h1>
            <span class="ed-head-spacer" aria-hidden="true"></span>
          </header>
          <div class="stack"><div class="empty" style="padding-top: 72px;">
            <div class="empty-icon">🖼️</div>
            <p class="muted" style="max-width: 280px;">The generation finished, but the result file isn\u2019t ready yet.</p>
            <button class="btn btn-secondary" data-action="retry">Check again</button>
          </div></div>
        </div>`;
      bindBack();
      root.querySelector('[data-action="retry"]')?.addEventListener('click', () => void boot());
      return;
    }
    if (!alive() || !meta?.url) {
      if (alive()) loadErrorHtml('network');
      return;
    }

    const url = meta.url;
    const isVideo = (meta.contentType || '').startsWith('video/');

    // Before/After: the caller's own input photo (full size, so sharpness differences show).
    let beforeUrl: string | null = null;
    const inputId = job.inputFileIds?.length === 1 ? job.inputFileIds[0] : null;
    if (!isVideo && inputId && COMPARE_TOOLS.has(job.tool)) {
      try {
        const m = await api.getUpload(inputId);
        beforeUrl = m.url ?? m.thumbnails?.thumb1024 ?? null;
      } catch {
        beforeUrl = null; // fall back to the plain result image
      }
      if (!alive()) return;
    }
    const ratio = meta.width && meta.height ? `${meta.width} / ${meta.height}` : '4 / 5';
    const poster = meta.thumbnails?.thumb1024 ?? meta.thumbnails?.thumb256 ?? undefined;
    const filename = `loomia-${job.id.slice(0, 8)}.${extFor(meta.contentType || '')}`;
    const when = formatDate(job.updatedAt);
    const kindLabel = isVideo ? 'Video' : 'Image';

    root.innerHTML = `
      <div class="rs">
        <header class="rs-head">
          <button class="ed-back" data-action="back" aria-label="Back">←</button>
          <h1 class="ed-title">Result</h1>
          <span class="ed-head-spacer" aria-hidden="true"></span>
        </header>
        <div class="stack rs-body">
          ${
            beforeUrl
              ? compareSliderHtml({
                  before: beforeUrl,
                  after: url,
                  ratio,
                  className: 'rs-compare',
                  id: 'rs-media',
                  beforeAlt: 'Original photo',
                  afterAlt: 'Generated image result',
                })
              : `<div class="rs-media" id="rs-media">
            ${
              isVideo
                ? `
              <video
                id="rs-video"
                src="${escapeHtml(url)}"
                ${poster ? `poster="${escapeHtml(poster)}"` : ''}
                playsinline
                preload="metadata"
                aria-label="Generated video result"
              ></video>
              <button class="rs-play" id="rs-play" aria-label="Play video">
                <span aria-hidden="true">▶</span>
              </button>`
                : `<img id="rs-img" src="${escapeHtml(url)}" alt="Generated image result" />`
            }
          </div>`
          }

          <div class="rs-saved">
            <span class="rs-saved-check" aria-hidden="true">✓</span>
            <span>Saved to Collection</span>
            <a class="rs-saved-link" href="#/collection">View →</a>
          </div>

          <p class="muted rs-meta">${escapeHtml(toolLabel(job.tool))} · ${escapeHtml(kindLabel)}${job.qualityTier ? ` · ${escapeHtml(job.qualityTier.toUpperCase())}` : ''}${job.durationSec ? ` · ${job.durationSec}s` : ''}${when ? ` · ${escapeHtml(when)}` : ''}</p>

          <div class="rs-actions">
            <button class="btn btn-primary" id="rs-download">${icon('download')} Download</button>
            <button class="btn btn-secondary" id="rs-share">Share</button>
          </div>
          <button class="btn btn-secondary btn-block" id="rs-retry">↻ Try again</button>
          <details class="rs-report" id="rs-report">
            <summary class="muted">Report this result</summary>
            <p>What’s wrong with it?</p>
            ${[
              ['sexual', 'Sexual or nude content'],
              ['minor', 'Involves a child'],
              ['violence', 'Violent or harmful'],
              ['hate', 'Hateful or harassing'],
              ['deceptive', 'Misleading or impersonation'],
              ['other', 'Something else'],
            ].map(([k, l]) => `<button class="btn btn-secondary btn-block" data-report="${k}">${l}</button>`).join('')}
          </details>
        </div>
      </div>`;
    bindBack();

    // -- Before/After slider (shared component) -----------------------------
    bindCompareSliders(root);

    // -- Viewport-aware video: pause when scrolled off-screen ----------------
    const video = root.querySelector<HTMLVideoElement>('#rs-video');
    const playBtn = root.querySelector<HTMLButtonElement>('#rs-play');
    if (video && playBtn) {
      const syncPlayBtn = (): void => {
        playBtn.classList.toggle('is-hidden', !video.paused);
      };
      video.addEventListener('play', syncPlayBtn);
      video.addEventListener('pause', syncPlayBtn);
      video.addEventListener('ended', syncPlayBtn);
      const toggle = (): void => {
        if (video.paused) void video.play().catch(() => undefined);
        else video.pause();
      };
      video.addEventListener('click', toggle);
      playBtn.addEventListener('click', toggle);
      observer = new IntersectionObserver(
        (entries) => {
          for (const e of entries) {
            if (!e.isIntersecting && alive() && !video.paused) video.pause();
          }
        },
        { threshold: 0.4 },
      );
      observer.observe(video);
    }

    // -- Report offensive output (Google Play AI-Generated Content policy) ------
    const reportBox = root.querySelector<HTMLDetailsElement>('#rs-report');
    reportBox?.querySelectorAll<HTMLButtonElement>('[data-report]').forEach((b) =>
      b.addEventListener('click', async () => {
        try {
          await api.reportJob(job.id, b.dataset.report!);
          reportBox.innerHTML = '<summary class="muted">Reported — thank you</summary>';
          toast('Thanks — we’ll review this result.');
        } catch (err) {
          toastApiError(err, 'Couldn’t send the report. Please try again.');
        }
      }),
    );

    // -- Download: fetch blob → object URL → a[download] ----------------------
    root.querySelector('#rs-download')?.addEventListener('click', async () => {
      const btn = root.querySelector<HTMLButtonElement>('#rs-download');
      if (btn) btn.disabled = true;
      try {
        // no-store: the <video>/<img> above loaded this URL without CORS, and the browser
        // may reuse that cached copy (no Access-Control-Allow-Origin) for this CORS fetch.
        const res = await fetch(url, { cache: 'no-store' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        const obj = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = obj;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.setTimeout(() => URL.revokeObjectURL(obj), 8000);
        toast('Download started.');
      } catch {
        // Fallback: open the file itself; the browser's own save works there.
        const w = window.open(url, '_blank');
        if (w) w.opener = null;
        else toast('Download failed. Please try again.');
      } finally {
        if (alive()) {
          const b = root.querySelector<HTMLButtonElement>('#rs-download');
          if (b) b.disabled = false;
        }
      }
    });

    // -- Share: Web Share API, fallback to copy-link --------------------------
    root.querySelector('#rs-share')?.addEventListener('click', async () => {
      const nav = navigator as ShareNavigator;
      if (typeof nav.share === 'function') {
        try {
          await nav.share({
            title: 'My Loomia creation',
            text: `Made with Loomia — ${toolLabel(job.tool)}`,
            url,
          });
        } catch (err) {
          // AbortError = user dismissed the sheet; stay silent.
          if (err instanceof Error && err.name !== 'AbortError') {
            toast('Sharing failed. Please try again.');
          }
        }
        return;
      }
      try {
        await navigator.clipboard.writeText(url);
        toast('Link copied to clipboard.');
      } catch {
        toast('Sharing isn\u2019t available in this browser.');
      }
    });

    // -- Try again: back to the editor with the same tool/template params -----
    root.querySelector('#rs-retry')?.addEventListener('click', () => {
      void (async () => {
        const params = new URLSearchParams();
        if (job.qualityTier) params.set('quality', job.qualityTier);
      if (job.durationSec) params.set('duration', String(job.durationSec));
        if (job.durationSec) params.set('duration', String(job.durationSec));
        if (job.templateId) {
          try {
            const page = await api.listTemplates({ tool: job.tool, limit: 100 });
            const tpl = page.templates.find((t) => t.id === job.templateId);
            if (tpl && alive()) params.set('template', tpl.slug);
          } catch {
            // Best-effort: fall through to tool-only.
          }
        }
        if (!alive()) return;
        const qs = params.toString();
        navigate(`/editor/${job.tool}${qs ? `?${qs}` : ''}`);
      })();
    });
  };

  // -- Failed / cancelled ---------------------------------------------------

  const showTerminalError = (job: AiJob): void => {
    const cancelled = job.state === 'cancelled';
    const headline = cancelled ? 'Generation cancelled' : 'Something went wrong';
    const detail = job.error?.trim() || 'The AI couldn\u2019t complete this generation.';
    root.innerHTML = `
      <div class="rs">
        <header class="rs-head">
          <button class="ed-back" data-action="back" aria-label="Back">←</button>
          <h1 class="ed-title">Result</h1>
          <span class="ed-head-spacer" aria-hidden="true"></span>
        </header>
        <div class="stack">
          <div class="empty" style="padding-top: 72px;">
            <div class="empty-icon">${cancelled ? '🚫' : '😕'}</div>
            <h2 class="rs-headline" style="margin: 0;">${escapeHtml(headline)}</h2>
            <p class="muted" style="max-width: 280px;">${escapeHtml(detail)}</p>
            <p class="rs-refund"><span aria-hidden="true">✦</span> ${job.creditCost} refunded — no credits were charged.</p>
            <button class="btn btn-primary" data-action="retry-gen">Try again</button>
            <a class="btn btn-secondary" href="#/home">Back home</a>
          </div>
        </div>
      </div>`;
    bindBack();
    root.querySelector('[data-action="retry-gen"]')?.addEventListener('click', () => {
      const params = new URLSearchParams();
      if (job.qualityTier) params.set('quality', job.qualityTier);
      if (job.durationSec) params.set('duration', String(job.durationSec));
      // Template slug lookup needs a network round-trip; keep the failure
      // path instant by reusing the tool route (editor shows template chips).
      const qs = params.toString();
      navigate(`/editor/${job.tool}${qs ? `?${qs}` : ''}`);
    });
  };

  // -- Poll loop ------------------------------------------------------------

  const onTerminal = (job: AiJob): void => {
    stop();
    refreshBalanceOnce();
    if (!alive()) return;
    if (job.state === 'done') void showDone(job);
    else showTerminalError(job);
  };

  const poll = async (): Promise<void> => {
    if (!alive()) return;
    try {
      const { job } = await api.getJob(jobId);
      if (!alive()) return;
      if (isTerminal(job.state)) {
        onTerminal(job);
        return;
      }
      paintActive(job);
    } catch (err) {
      if (!alive()) return;
      stop();
      if (err instanceof ApiError && err.status === 404) loadErrorHtml('notfound');
      else if (err instanceof ApiError && err.status === 403) loadErrorHtml('forbidden');
      else {
        loadErrorHtml('network');
        toastApiError(err, 'Connection lost while checking progress.');
      }
      return;
    }
    if (alive()) timer = setTimeout(() => void poll(), POLL_MS);
  };

  const boot = (): void => {
    stop();
    balanceRefreshed = false;
    loadingHtml();
    void poll();
  };

  boot();
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  try {
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}
